import { Injectable, OnDestroy } from '@angular/core';
import { UtilService } from './util.service';
import { log } from 'src/app/core/log';

/** Recordado en `sessionStorage`: sobrevive a la recarga tras un deploy, no a cerrar la pestaña. */
const SHIFT_KEY = 'shiftOpen';
const ALERT_SOUND = 'assets/alert.mp3';
/** Cada cuanto alterna el titulo de la pestaña mientras hay pedidos sin atender. */
const TITLE_BLINK_MS = 1000;
const VIBRATION_PATTERN = [300, 150, 300];

/** Lo que se usa del Wake Lock: solo soltarlo. */
interface ScreenLock {
  release(): Promise<void>;
}

/**
 * El turno de la comanda: lo que hace falta para que un pedido nuevo se escuche.
 *
 * El navegador no deja sonar audio, pedir Wake Lock ni (en iOS) pedir permiso de
 * notificaciones sin un gesto del usuario. Por eso todo eso pasa en `open()`, que se llama
 * desde el toque de «Abrir turno». Despues, `startAlert()` hace sonar `alert.mp3` en bucle,
 * alterna el titulo de la pestaña, vibra y, con la pestaña en segundo plano, muestra una
 * `Notification` local.
 *
 * La vibracion, como el audio, necesita un toque previo en Chrome: tras una recarga no
 * vibra. Por eso la pantalla de «Abrir turno» de la lista es el aviso de respaldo.
 *
 * §13.8 del diseño: una recarga (por ejemplo la de `UpdateWatcherService` tras un deploy)
 * vuelve a bloquear el audio. `wasOpen` dice que el turno estaba abierto, para que la
 * pagina pida «Abrir turno» a pantalla completa; mientras tanto el aviso va sin sonido.
 */
@Injectable({ providedIn: 'root' })
export class ShiftService implements OnDestroy {
  /** El turno se abrio en esta carga de la pagina: el audio esta desbloqueado. */
  isOpen = false;

  private audio: HTMLAudioElement | null = null;
  private wakeLock: ScreenLock | null = null;
  private blink: ReturnType<typeof setInterval> | null = null;
  private alertCount = 0;
  /** La apertura en curso: un doble toque espera a la misma en vez de crear otro audio. */
  private opening: Promise<void> | null = null;
  private baseTitle = '';
  private readonly onVisibility = () => {
    if (this.isOpen && document.visibilityState === 'visible') {
      void this.keepScreenOn();
    }
  };

  constructor(private util: UtilService) {}

  /** El turno estaba abierto antes de que la pagina se recargara. */
  get wasOpen(): boolean {
    return sessionStorage.getItem(SHIFT_KEY) === '1';
  }

  /**
   * Abre el turno. Tiene que llamarse desde un toque del usuario. Un segundo toque mientras
   * se abre espera a la misma apertura: antes creaba otro audio y el primero quedaba sonando
   * en bucle sin que nada lo parara.
   */
  open(): Promise<void> {
    if (this.isOpen) {
      return Promise.resolve();
    }
    this.opening ??= this.unlock().finally(() => (this.opening = null));
    return this.opening;
  }

  private async unlock(): Promise<void> {
    const audio = this.createAudio();
    audio.loop = true;
    this.audio = audio;
    // Todo lo que exige el gesto sale antes del primer `await`: despues ya no cuenta como
    // parte del toque (iOS lo exige para el permiso de notificaciones).
    const sound = audio
      .play()
      .then(() => {
        // Sonar y cortar dentro del gesto deja el audio desbloqueado para despues.
        audio.pause();
        audio.currentTime = 0;
      })
      .catch((error) => log('el navegador no dejo desbloquear el audio', error));
    const screen = this.keepScreenOn();
    const permission = this.askNotificationPermission().catch((error) =>
      log('no se pudo pedir permiso de notificaciones', error)
    );
    await Promise.all([sound, screen, permission]);
    this.isOpen = true;
    sessionStorage.setItem(SHIFT_KEY, '1');
    document.addEventListener('visibilitychange', this.onVisibility);
    if (this.alertCount) {
      void this.playLoop();
    }
  }

  /** Suelta las escuchas y el titulo, sin olvidar el turno (no es cerrarlo). */
  ngOnDestroy(): void {
    this.stopAlert();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  close(): void {
    this.stopAlert();
    this.isOpen = false;
    sessionStorage.removeItem(SHIFT_KEY);
    document.removeEventListener('visibilitychange', this.onVisibility);
    void this.wakeLock?.release().catch((error) => log('no se pudo soltar el Wake Lock', error));
    this.wakeLock = null;
  }

  /** Avisa que hay `count` pedidos nuevos sin atender. Con 0, deja de avisar. */
  startAlert(count: number): void {
    if (count <= 0) {
      this.stopAlert();
      return;
    }
    const arrived = count > this.alertCount;
    this.alertCount = count;
    if (this.isOpen) {
      void this.playLoop();
    }
    if (arrived) {
      this.vibrate(VIBRATION_PATTERN);
      // Sin el foco, aunque se vea (otra ventana en la PC), el sonido puede no alcanzar.
      if (document.visibilityState === 'hidden' || !document.hasFocus()) {
        this.notify(this.util.translate('New order'), `${this.util.translate('Pending orders')}: ${count}`);
      }
    }
    if (!this.blink) {
      this.baseTitle = document.title;
      this.blink = setInterval(() => this.toggleTitle(), TITLE_BLINK_MS);
    }
  }

  stopAlert(): void {
    this.alertCount = 0;
    if (this.audio && !this.audio.paused) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    if (this.blink) {
      clearInterval(this.blink);
      this.blink = null;
      document.title = this.baseTitle;
    }
  }

  protected createAudio(): HTMLAudioElement {
    return new Audio(ALERT_SOUND);
  }

  protected async requestWakeLock(): Promise<ScreenLock | null> {
    return 'wakeLock' in navigator ? navigator.wakeLock.request('screen') : null;
  }

  protected async askNotificationPermission(): Promise<void> {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }

  protected vibrate(pattern: number[]): void {
    navigator.vibrate?.(pattern);
  }

  /** Una notificacion local. En Chrome para Android `new Notification()` lanza sin service worker. */
  protected notify(title: string, body: string): void {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(title, { body });
        // Tocarla trae la pestaña de la comanda.
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
    } catch (error) {
      log('el navegador no deja mostrar la notificacion', error);
    }
  }

  private async playLoop(): Promise<void> {
    if (this.audio?.paused) {
      try {
        await this.audio.play();
      } catch (error) {
        log('no se pudo hacer sonar la alerta', error);
      }
    }
  }

  private async keepScreenOn(): Promise<void> {
    try {
      this.wakeLock = await this.requestWakeLock();
    } catch (error) {
      log('el navegador no dio el Wake Lock', error);
    }
  }

  private toggleTitle(): void {
    const alertTitle = `(${this.alertCount}) ${this.util.translate('New order')}`;
    document.title = document.title === alertTitle ? this.baseTitle : alertTitle;
  }
}
